import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Check, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiError } from "../../lib/api";

export default function PortalMessages() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/messages");
      setHistory(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const s = subject.trim();
    const m = message.trim();
    if (!s || !m) return toast.error("Asunto y mensaje son obligatorios");
    if (s.length > 200) return toast.error("Asunto demasiado largo");
    if (m.length > 5000) return toast.error("Mensaje demasiado largo");
    setLoading(true);
    try {
      await api.post("/portal/messages", { subject: s, message: m });
      toast.success("Mensaje enviado a tu asesor");
      setSubject("");
      setMessage("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al enviar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#121214] border border-white/5 rounded-2xl overflow-hidden" data-testid="portal-messages">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
          <div className="font-serif text-xl text-white">Escríbenos</div>
        </div>
        <div className="text-xs text-zinc-500">Tu asesor recibe un aviso al instante</div>
      </div>

      <form onSubmit={submit} className="p-6 border-b border-white/5 space-y-4" data-testid="portal-message-form">
        <div>
          <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Asunto</label>
          <input
            required
            value={subject}
            maxLength={200}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ej: Duda sobre modelo 303 Q2"
            data-testid="portal-msg-subject"
            className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">Mensaje</label>
          <textarea
            required
            rows={5}
            value={message}
            maxLength={5000}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Cuéntanos qué necesitas…"
            data-testid="portal-msg-body"
            className="w-full bg-transparent border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#D4AF37] focus:outline-none resize-y"
          />
          <div className="text-xs text-zinc-600 mt-1">{message.length} / 5000</div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="portal-msg-send"
          className="px-6 py-2.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
        >
          <Send className="w-4 h-4" /> {loading ? "Enviando…" : "Enviar mensaje"}
        </button>
      </form>

      <div className="p-6">
        <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">Historial de mensajes</div>
        {history.length ? (
          <div className="space-y-3">
            {history.map((h, i) => (
              <motion.div
                key={h.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="border border-white/5 rounded-lg p-4"
                data-testid={`portal-history-${h.id}`}
              >
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="font-medium text-white">{h.subject}</div>
                  {h.answered ? (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 inline-flex items-center gap-1">
                      <Check className="w-3 h-3" /> Contestado
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Enviado
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-400 whitespace-pre-wrap break-words">{h.message}</p>
                <div className="text-xs text-zinc-600 font-mono mt-2">
                  {new Date(h.created_at).toLocaleString("es-ES")}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-zinc-500 italic">Aún no has enviado mensajes.</div>
        )}
      </div>
    </div>
  );
}
